<script lang="ts" setup>
import { Toaster as Sonner, type ToasterProps } from 'vue-sonner'

const props = withDefaults(defineProps<ToasterProps>(), {
  richColors: false,
})

/** Wired on the toaster root; `--toast-accent-border` in `app.css` is a neutral success green for Sonner only. */
const sonnerCssVars = {
  '--normal-bg': 'var(--popover)',
  '--normal-text': 'var(--popover-foreground)',
  '--normal-border': 'var(--border)',
  '--success-border': 'var(--toast-accent-border)',
  '--warning-border': 'var(--toast-warning-border)',
  '--info-border': 'var(--toast-info-border)',
  '--error-border': 'var(--toast-destructive-border)',
} as const
</script>

<template>
  <Sonner class="toaster group" v-bind="props" :style="sonnerCssVars" />
</template>

<!-- Reka UI's modal `DialogContent` sets `body { pointer-events: none }` while
     open, which silently blocks clicks on Sonner toasts (Sonner mounts the
     toaster as a body descendant). Force the toaster + every toast it renders
     to keep `pointer-events: auto` so the close button is always clickable
     regardless of which dialog is open. -->
<style>
[data-sonner-toaster],
[data-sonner-toaster] * {
  pointer-events: auto !important;
}

/*
 * Unscoped: Sonner renders toasts in a portal.
 * Descendant `[data-icon]` — icon is a child, not on the toast root.
 */
[data-sonner-toast][data-type='success'] {
  border: 1px solid var(--toast-accent-border) !important;
}
[data-sonner-toast][data-type='success'] [data-icon],
[data-sonner-toast][data-type='success'] [data-icon] svg {
  color: var(--toast-accent-border) !important;
}

[data-sonner-toast][data-type='warning'] {
  border: 1px solid var(--toast-warning-border) !important;
}
[data-sonner-toast][data-type='warning'] [data-icon],
[data-sonner-toast][data-type='warning'] [data-icon] svg {
  color: var(--warning) !important;
}

[data-sonner-toast][data-type='info'] {
  border: 1px solid var(--toast-info-border) !important;
}
[data-sonner-toast][data-type='info'] [data-icon],
[data-sonner-toast][data-type='info'] [data-icon] svg {
  color: var(--info) !important;
}

[data-sonner-toast][data-type='error'] {
  border: 1px solid var(--toast-destructive-border) !important;
}
[data-sonner-toast][data-type='error'] [data-icon],
[data-sonner-toast][data-type='error'] [data-icon] svg {
  color: var(--destructive) !important;
}
</style>
